import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AxiosInstance from "../../Service/AxiosInstance"; // Update this path as needed
import { Customer } from "../../components/UserManagement/UserManagement";

// Define the interface for the task
export interface Task {
  id: string;
  name: string;
  description: string;
  trackObject: boolean;
  colour: string;
}

// Define the interface for the domain
export interface Domain {
  id: string;
  name: string;
  description: string;
}

// Define the interface for customers

// Define the interface for logs
export interface Log {
  id: string;
  activity: string;
  customerId: string;
  parameter: string;
  userId: string;
  userName: string;
  timestamp: string;
  createdDate?: string;
}

// Define the initial state
interface TasksState {
  tasks: Task[];
  domains: Domain[];
  customers: Customer[];
  logs: Log[];
  loading: boolean;
  loadingLog: boolean;
  error: string | null;
}

const initialState: TasksState = {
  tasks: [],
  domains: [],
  customers: [],
  logs: [],
  loading: false,
  loadingLog: false,
  error: null,
};

// Create an async thunk for fetching tasks
export const fetchTasks = createAsyncThunk(
  "tasks/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get<Task[]>(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ControlPanel/GetTasks`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "An unknown error occurred"
      );
    }
  }
);

// Create an async thunk for fetching domains
export const fetchDomains = createAsyncThunk(
  "tasks/fetchDomains",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get<Domain[]>(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ControlPanel/GetDomain`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "An unknown error occurred"
      );
    }
  }
);

// Create an async thunk for fetching customers
export const fetchCustomers = createAsyncThunk(
  "tasks/fetchCustomers",
  async (searchTerm: string, { rejectWithValue }) => {
    try {
      console.log(rejectWithValue, "rejectWithValue");
      const response = await AxiosInstance.get<Customer[]>(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ActivityLogs/GetAllCustomerUsers`,
        {
          params: { search: searchTerm }, // Pass search term as query param
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "An unknown error occurred"
      );
    }
  }
);

// Create an async thunk for fetching logs
export const fetchLogs = createAsyncThunk(
  "tasks/fetchLogs",
  async (
    {
      userId,
      startDate,
      endDate,
    }: {
      userId: string | null;
      startDate: string | null;
      endDate: string | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();

      // Add UserId if userId is not null
      if (userId) {
        queryParams.append("UserId", userId);
      }
      ///queryParams.append("UserId", "44976694-B074-4F77-9F53-50A7408EA8F9"); //for test the result.

      // Add StartDate and EndDate only if StartDate is present
      if (startDate) {
        queryParams.append("StartDate", startDate);
        if (endDate) {
          queryParams.append("EndDate", endDate);
        }
      }

      const response = await AxiosInstance.get<Log[]>(
        `${
          process.env.REACT_APP_SERVER_BASEURL
        }/api/ActivityLogs/GetAllLogs?${queryParams.toString()}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "An unknown error occurred"
      );
    }
  }
);

// Create a slice
const ProjectTasksSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    clearTasks: (state) => {
      state.tasks = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.tasks = [];
      })
      .addCase(fetchDomains.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDomains.fulfilled, (state, action) => {
        state.loading = false;
        state.domains = action.payload;
      })
      .addCase(fetchDomains.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.domains = [];
      })
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.customers = [];
      })
      .addCase(fetchLogs.pending, (state) => {
        state.loadingLog = true;
        state.error = null;
      })
      .addCase(fetchLogs.fulfilled, (state, action) => {
        state.loadingLog = false;
        state.logs = action.payload;
      })
      .addCase(fetchLogs.rejected, (state, action) => {
        state.loadingLog = false;
        state.error = action.payload as string;
        state.logs = [];
      });
  },
});

// Export the actions and the reducer
export const { clearTasks } = ProjectTasksSlice.actions;
export default ProjectTasksSlice.reducer;
